import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Divider,
    Grid,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

const getDefaultRange = () => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 29);
    return {
        fromDate: fromDate.toISOString().slice(0, 10),
        toDate: toDate.toISOString().slice(0, 10),
    };
};

const formatCurrency = (value) => {
    const safeValue = Number(value || 0);
    return new Intl.NumberFormat('en-LK', {
        style: 'currency',
        currency: 'LKR',
        maximumFractionDigits: 0,
    }).format(safeValue);
};

const formatShopName = (shop) => {
    if (shop === null || shop === undefined) {
        return 'Unknown';
    }
    const normalized = String(shop).trim().toLowerCase();
    if (normalized === '1' || normalized.includes('ratna')) {
        return 'Ratnapura';
    }
    if (normalized === '2' || normalized.includes('kala')) {
        return 'Kalawana';
    }
    if (normalized === '3' || normalized.includes('palm')) {
        return 'Pelmadulla';
    }
    return String(shop);
};

const downloadCsv = (filename, columns, rows) => {
    const header = columns.map((column) => `"${column.label}"`).join(',');
    const dataLines = rows.map((row) =>
        columns
            .map((column) => {
                const value = row[column.key] ?? '';
                const safeValue = String(value).replace(/"/g, '""');
                return `"${safeValue}"`;
            })
            .join(',')
    );
    const csvContent = [header, ...dataLines].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function ReportDashboard() {
    const [filters, setFilters] = useState(getDefaultRange());
    const [appliedFilters, setAppliedFilters] = useState(filters);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reportData, setReportData] = useState({
        range: filters,
        summary: { totalInvoices: 0, totalSales: 0 },
        salesTrend: [],
        salesByBranch: [],
        topItems: [],
        counts: {
            totalItems: 0,
            totalSuppliers: 0,
            totalDoctors: 0,
            totalPets: 0,
            totalOwners: 0,
        },
    });

    const fetchReport = async (range) => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('https://serverfourpaws.ccsoftwares.com/reports/overview', {
                params: range,
            });
            setReportData(response.data);
        } catch (err) {
            setError('Unable to load report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport(appliedFilters);
    }, [appliedFilters]);

    const salesTrendLabels = useMemo(
        () => reportData.salesTrend.map((row) => row.salesDate),
        [reportData.salesTrend]
    );
    const salesTrendValues = useMemo(
        () => reportData.salesTrend.map((row) => Number(row.totalSales || 0)),
        [reportData.salesTrend]
    );
    const branchLabels = useMemo(
        () => reportData.salesByBranch.map((row) => formatShopName(row.shop)),
        [reportData.salesByBranch]
    );
    const branchValues = useMemo(
        () => reportData.salesByBranch.map((row) => Number(row.totalSales || 0)),
        [reportData.salesByBranch]
    );
    const topItemLabels = useMemo(
        () => reportData.topItems.map((row) => row.itemName),
        [reportData.topItems]
    );
    const topItemValues = useMemo(
        () => reportData.topItems.map((row) => Number(row.totalSales || 0)),
        [reportData.topItems]
    );

    const summaryCards = [
        {
            label: 'Net Sales',
            value: formatCurrency(reportData.summary.totalSales),
            tone: 'linear-gradient(135deg, #DFF6F4 0%, #F5FFFE 100%)',
        },
        {
            label: 'Total Invoices',
            value: reportData.summary.totalInvoices || 0,
            tone: 'linear-gradient(135deg, #FEEFCB 0%, #FFF9E7 100%)',
        },
        {
            label: 'Active Items',
            value: reportData.counts.totalItems || 0,
            tone: 'linear-gradient(135deg, #E3F3FF 0%, #F6FBFF 100%)',
        },
        {
            label: 'Active Suppliers',
            value: reportData.counts.totalSuppliers || 0,
            tone: 'linear-gradient(135deg, #F7E6FF 0%, #FBF6FF 100%)',
        },
        {
            label: 'Total Pets',
            value: reportData.counts.totalPets || 0,
            tone: 'linear-gradient(135deg, #E7FFE9 0%, #F6FFF7 100%)',
        },
        {
            label: 'Pet Owners',
            value: reportData.counts.totalOwners || 0,
            tone: 'linear-gradient(135deg, #FFE6E6 0%, #FFF5F5 100%)',
        },
    ];

    const handleFilterChange = (event) => {
        setFilters((prev) => ({
            ...prev,
            [event.target.name]: event.target.value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };

    const handleResetFilters = () => {
        const defaults = getDefaultRange();
        setFilters(defaults);
        setAppliedFilters(defaults);
    };

    const downloadSalesTrend = () => {
        downloadCsv(
            'sales-trend',
            [
                { label: 'Date', key: 'salesDate' },
                { label: 'Net Sales (LKR)', key: 'totalSales' },
            ],
            reportData.salesTrend
        );
    };

    const downloadBranchSales = () => {
        const branchRows = reportData.salesByBranch.map((row) => ({
            shop: formatShopName(row.shop),
            invoiceCount: row.invoiceCount,
            totalSales: row.totalSales,
        }));
        downloadCsv(
            'branch-sales',
            [
                { label: 'Branch', key: 'shop' },
                { label: 'Invoice Count', key: 'invoiceCount' },
                { label: 'Net Sales (LKR)', key: 'totalSales' },
            ],
            branchRows
        );
    };

    const downloadTopItems = () => {
        downloadCsv(
            'top-items',
            [
                { label: 'Item', key: 'itemName' },
                { label: 'Total Quantity', key: 'totalQuantity' },
                { label: 'Net Sales (LKR)', key: 'totalSales' },
            ],
            reportData.topItems
        );
    };

    return (
        <Container maxWidth={false} sx={{ pb: 4 }}>
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #012A2D 0%, #055E57 100%)',
                    color: 'white',
                    borderRadius: 3,
                    p: { xs: 2.5, md: 3.5 },
                    mb: 3,
                    boxShadow: '0 20px 40px rgba(1, 42, 45, 0.2)',
                }}
            >
                <Typography
                    sx={{
                        fontSize: { xs: '26px', md: '34px' },
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        fontFamily: 'Oswald, sans-serif',
                    }}
                >
                    Performance Insights
                </Typography>
                <Typography sx={{ opacity: 0.85, mt: 0.5, fontFamily: 'Kanit, sans-serif' }}>
                    Track revenue, demand, and operational health across all branches.
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label={`Range: ${reportData.range.fromDate} to ${reportData.range.toDate}`}
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
                        variant="outlined"
                    />
                    <Chip
                        label={`Invoices: ${reportData.summary.totalInvoices || 0}`}
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
                        variant="outlined"
                    />
                    <Chip
                        label={`Net Sales: ${formatCurrency(reportData.summary.totalSales)}`}
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
                        variant="outlined"
                    />
                </Box>
            </Box>

            <Card sx={{ borderRadius: 3, mb: 3, boxShadow: '0 10px 25px rgba(0,0,0,0.06)' }}>
                <CardHeader
                    title="Report Filters"
                    titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600 }}
                    action={
                        <Button
                            variant="outlined"
                            startIcon={<RefreshOutlinedIcon />}
                            onClick={handleResetFilters}
                            sx={{ borderRadius: 2 }}
                        >
                            Reset
                        </Button>
                    }
                />
                <Divider />
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="From Date"
                                type="date"
                                name="fromDate"
                                value={filters.fromDate}
                                onChange={handleFilterChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: filters.toDate }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="To Date"
                                type="date"
                                name="toDate"
                                value={filters.toDate}
                                onChange={handleFilterChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: filters.fromDate }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                sx={{ borderRadius: 2, minWidth: 150 }}
                            >
                                Apply Filters
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {summaryCards.map((card) => (
                    <Grid item xs={12} sm={6} md={4} key={card.label}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                background: card.tone,
                                boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                            }}
                        >
                            <CardContent>
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        fontWeight: 600,
                                        color: '#224043',
                                    }}
                                >
                                    {card.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '28px',
                                        fontWeight: 700,
                                        mt: 1,
                                        fontFamily: 'Oswald, sans-serif',
                                    }}
                                >
                                    {card.value}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 12px 24px rgba(0,0,0,0.08)' }}>
                        <CardHeader
                            title="Daily Net Sales Trend"
                            titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600 }}
                            action={
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    onClick={downloadSalesTrend}
                                    sx={{ borderRadius: 2 }}
                                    disabled={!reportData.salesTrend.length}
                                >
                                    CSV
                                </Button>
                            }
                        />
                        <Divider />
                        <CardContent>
                            <LineChart
                                height={300}
                                series={[
                                    {
                                        data: salesTrendValues,
                                        label: 'Net Sales',
                                        color: '#02847A',
                                        valueFormatter: (value) => formatCurrency(value),
                                    },
                                ]}
                                xAxis={[
                                    {
                                        scaleType: 'point',
                                        data: salesTrendLabels,
                                    },
                                ]}
                                grid={{ vertical: true, horizontal: true }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, height: '100%', boxShadow: '0 12px 24px rgba(0,0,0,0.08)' }}>
                        <CardHeader
                            title="Sales by Branch"
                            titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600 }}
                            action={
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    onClick={downloadBranchSales}
                                    sx={{ borderRadius: 2 }}
                                    disabled={!reportData.salesByBranch.length}
                                >
                                    CSV
                                </Button>
                            }
                        />
                        <Divider />
                        <CardContent>
                            <BarChart
                                height={300}
                                series={[
                                    {
                                        data: branchValues,
                                        label: 'Net Sales',
                                        color: '#0E7C7B',
                                        valueFormatter: (value) => formatCurrency(value),
                                    },
                                ]}
                                xAxis={[
                                    {
                                        data: branchLabels,
                                        scaleType: 'band',
                                    },
                                ]}
                                margin={{ top: 20, bottom: 40, left: 30, right: 10 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 12px 24px rgba(0,0,0,0.08)' }}>
                        <CardHeader
                            title="Top 10 Items by Revenue"
                            titleTypographyProps={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600 }}
                            action={
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    onClick={downloadTopItems}
                                    sx={{ borderRadius: 2 }}
                                    disabled={!reportData.topItems.length}
                                >
                                    CSV
                                </Button>
                            }
                        />
                        <Divider />
                        <CardContent>
                            <BarChart
                                height={340}
                                series={[
                                    {
                                        data: topItemValues,
                                        label: 'Revenue',
                                        color: '#4A6D7C',
                                        valueFormatter: (value) => formatCurrency(value),
                                    },
                                ]}
                                xAxis={[
                                    {
                                        data: topItemLabels,
                                        scaleType: 'band',
                                    },
                                ]}
                                margin={{ top: 20, bottom: 60, left: 40, right: 10 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}

export default ReportDashboard;
